import { DataType, FilterType, UserPermission } from '@globaleris/ui.library'
import {
  DataTableColumn,
  DataTableOptions,
  GlobDataTable,
  GroupCustomAction
} from '@globaleris/ui.library/build/components/globalerisDataTable'
import { makeStyles, useTheme } from '@material-ui/core'
import { Warning } from '@material-ui/icons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumEstadoFactura } from '../../../../api'
import { AdjustmentConceptTableModel } from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import { CardBottom } from '../../../../components/shared/cardBottom/cardBottom'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../helpers'
import { styles } from '../../settlements.styles'

interface AdjustmentConceptsTableCardProps {
  data: AdjustmentConceptTableModel[] | undefined
  isLoading?: boolean
  show?: boolean
  title?: string
  elevation?: number

  // Acción de grupo (liquidación agrupada): con checkbox
  onViewObjectionsClick?: (row: AdjustmentConceptTableModel) => void

  // Footer
  customFooterActions?: CardActionProps[]
}

export const AdjustmentConceptsTableCard: React.FC<AdjustmentConceptsTableCardProps> = ({
  data,
  isLoading,
  show = true,
  title,
  elevation,
  onViewObjectionsClick,
  customFooterActions = []
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements', 'settlementsInvoices'])
  const theme = useTheme()
  const useStyles = makeStyles(styles(theme))
  const classes = useStyles()

  // #region Group actions (filas agrupadas — con checkbox)

  const groupCustomActions = useMemo((): GroupCustomAction[] => {
    if (!onViewObjectionsClick) return []
    return [
      {
        icon: <Warning fontSize="small" />,
        tooltip: t('settlementsInvoices:components.settlementsTableCard.tooltips.objections'),
        onClick: (_groupValue: any, leafRows: AdjustmentConceptTableModel[]) => {
          if (leafRows[0]) onViewObjectionsClick(leafRows[0])
        },
        userPermission: UserPermission.EDIT
      }
    ]
  }, [onViewObjectionsClick, t])

  // #endregion

  // #region DataTable columns

  const columns = useMemo(
    (): DataTableColumn[] => [
      {
        name: 'conceptId',
        type: DataType.NUMBER,
        display: false
      },
      {
        name: 'settlementId',
        type: DataType.NUMBER,
        display: false
      },
      {
        name: 'actions',
        label: '',
        hideForResponsive: true,
        type: DataType.HTML,
        columnWidth: 65
      },
      // --- Claves de agrupación
      {
        name: 'settlementGroupCode',
        label: t(
          'settlements:components.settlementsTableCard.dataTable.columns.settlementGroupCode'
        ),
        type: DataType.TEXT,
        display: true
      },
      {
        name: 'contractCode',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.contractCode'),
        type: DataType.TEXT,
        display: false
      },
      // --- Factura origen ---
      {
        name: 'invoiceCode',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.invoiceCode'),
        type: DataType.TEXT
      },
      {
        name: 'recipientSubject',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.recipientSubject'),
        type: DataType.TEXT,
        columnWidth: 200
      },
      {
        name: 'cups',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.cups'),
        type: DataType.TEXT
      },
      {
        name: 'issueDate',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.issueDate'),
        type: DataType.DATE
      },
      // --- Concepto ---
      {
        name: 'conceptCode',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.conceptCode'),
        type: DataType.TEXT
      },
      {
        name: 'conceptDescription',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.conceptDescription'),
        type: DataType.TEXT,
        columnWidth: 250
      },
      {
        name: 'startDate',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.startDate'),
        type: DataType.DATE
      },
      {
        name: 'endDate',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.endDate'),
        type: DataType.DATE
      },
      {
        name: 'amount',
        label: t('settlements:components.settlementsTableCard.dataTable.columns.amount'),
        type: DataType.NUMBER
      },
      {
        name: 'conceptState',
        label: t('settlements:components.adjustmentConceptsTableCard.columns.conceptState'),
        type: DataType.SELECT,
        selectOptions: enumToSelectOptionArray(EnumEstadoFactura, 'enums:EnumEstadoFactura')
      }
    ],
    [t]
  )

  // #endregion

  // #region DataTable options

  const expandedGroups = useMemo(() => {
    const groups: Record<string, boolean> = {}
    data?.forEach(row => {
      const key = row.settlementGroupCode ?? ''
      groups[`settlementGroupCode:${key}`] = true
    })
    return groups
  }, [data])

  const options = useMemo(
    (): DataTableOptions => ({
      search: true,
      pagination: true,
      rowsPerPage: 100,
      rowsPerPageOptions: [20, 50, 100],
      filterType: FilterType.ADVANCED,
      // Checkboxes sólo en el nivel de grupo, no en filas individuales
      selectableRows: true,
      selectMode: 'group',
      grouping: ['settlementGroupCode', 'invoiceCode'],
      expanded: expandedGroups,
      groupCustomActions: groupCustomActions.length > 0 ? groupCustomActions : undefined
    }),
    [groupCustomActions, expandedGroups]
  )

  // #endregion

  return (
    <CardWithTransition show={show} elevation={elevation} className={classes.card}>
      <GlobDataTable
        title={title}
        data={data}
        columns={columns}
        options={options}
        rowIdField="conceptId"
        isLoading={isLoading}
      />
      {customFooterActions.length > 0 && <CardBottom actions={customFooterActions} />}
    </CardWithTransition>
  )
}
